import React, { createContext, useState, ReactNode } from 'react';

// Save-bar contracts. These lived in components/SaveBar until that component
// (a never-rendered legacy bottom bar) was deleted — the shell's top bar is
// the only renderer now, reading `current` from this context.
export interface SaveBarAction {
    onAction: () => void;
    loading?: boolean;
    disabled?: boolean;
}

export interface SaveBarProps {
    message?: string | React.ReactNode;
    saveAction: SaveBarAction;
    discardAction: SaveBarAction;
    show: boolean;
    messageStatus?: 'info' | 'warning' | 'error';
}

// Define the context shape
export interface SaveBarContextProps {
    saveBar: (_props: SaveBarProps | undefined) => void;
    isActive: boolean;
    /** The currently-registered save-bar props (rendered by the app shell's top bar). */
    current?: SaveBarProps;
    discardAction?: SaveBarProps['discardAction'];
}

// Create the context with an undefined initial value
export const SaveBarContext = createContext<SaveBarContextProps | undefined>(undefined);

// Create a provider component. The save bar itself is rendered by the app shell
// (AppShell top bar) which reads `current` from this context — the provider no
// longer renders a fixed bottom bar.
export const SaveBarProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [saveBarProps, setSaveBarProps] = useState<SaveBarProps | undefined>(undefined);

    const saveBar = (props: SaveBarProps | undefined) => {
        setSaveBarProps(props);
    };

    return (
        <SaveBarContext.Provider
            value={{
                saveBar,
                isActive: saveBarProps?.show ?? false,
                current: saveBarProps,
                discardAction: saveBarProps?.discardAction,
            }}
        >
            {children}
        </SaveBarContext.Provider>
    );
};
